import { Fragment, MouseEvent, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { KB_BASE_URL } from '@/shared/config';

import { i18n } from '../config';
import type {
  KbIrInline,
  KbIrNode,
  KbIrTableCell,
  KbIrTextMark,
  KbIrTextStyles,
} from '../lib';
import { parseKbLearnHref } from '../lib/kbLinks';

import styles from './KnowledgeBase.module.scss';

type OnOpenArticle = (slug: string, market?: string) => void;

// Mark nesting order: bold outermost, then italic, underline, code —
// mirrors the hub renderer's mapping.
const MARK_ORDER: readonly KbIrTextMark[] = [
  'bold',
  'italic',
  'underline',
  'code',
];

const Marked = ({
  text,
  marks,
  styles,
}: {
  text: string;
  marks?: KbIrTextMark[];
  styles?: KbIrTextStyles;
}) => {
  const ordered = MARK_ORDER.filter((mark) => marks?.includes(mark));

  const content = ordered.reduceRight<ReactNode>(
    (child, mark) =>
      mark === 'bold' ? (
        <strong>{child}</strong>
      ) : mark === 'italic' ? (
        <em>{child}</em>
      ) : mark === 'underline' ? (
        <u>{child}</u>
      ) : (
        <code>{child}</code>
      ),
    text,
  );

  // TextState styling (contract @2) wraps the mark nesting.
  if (!styles?.color && !styles?.background) {
    return content;
  }

  return (
    <span
      style={{
        ...(styles.color ? { color: styles.color } : {}),
        ...(styles.background ? { backgroundColor: styles.background } : {}),
      }}
    >
      {content}
    </span>
  );
};

// Media whose backing file is unavailable must not render as a broken
// element; failed images collapse away (error events do not bubble, so
// each element carries its own handler).
const collapseOnError = (event: MouseEvent<HTMLImageElement>) => {
  event.currentTarget.style.display = 'none';
};

const renderInlines = (
  inlines: KbIrInline[],
  onOpenArticle: OnOpenArticle,
): ReactNode =>
  inlines.map((inline, index) => renderInline(inline, index, onOpenArticle));

const renderInline = (
  inline: KbIrInline,
  key: number,
  onOpenArticle: OnOpenArticle,
): ReactNode => {
  switch (inline.type) {
    case 'text':
      return (
        <Marked
          key={key}
          text={inline.text}
          marks={inline.marks}
          styles={inline.styles}
        />
      );
    case 'linebreak':
      return <br key={key} />;
    // Inline media (contract @2): compact forms, mirroring the hub's
    // standalone renderer — text-height image, chip links otherwise.
    case 'image':
      return (
        <img
          key={key}
          className={styles.inlineImage}
          src={inline.src}
          alt={inline.alt ?? ''}
          loading="lazy"
        />
      );
    case 'video':
      return (
        <a key={key} className={styles.inlineMediaChip} href={inline.src}>
          {`\u25B6 ${inline.filename || 'Video'}`}
        </a>
      );
    case 'youtube':
      return (
        <a
          key={key}
          className={styles.inlineMediaChip}
          href={`https://www.youtube.com/watch?v=${inline.videoId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {'\u25B6 YouTube'}
        </a>
      );
    case 'document':
      return (
        <a
          key={key}
          className={styles.inlineMediaChip}
          href={inline.downloadUrl}
        >
          {`\uD83D\uDCC4 ${inline.title || inline.filename || 'Document'}`}
        </a>
      );
    case 'link': {
      const children = renderInlines(inline.children, onOpenArticle);
      const parsed = parseKbLearnHref(inline.href, KB_BASE_URL);

      // The old sanitized path stripped javascript: and other non-web
      // schemes; hub-side validation covers this too, but the guarantee is
      // restored client-side: only http(s) hrefs — plus hub article links
      // checked via parseKbLearnHref — render as anchors. Anything else
      // renders its children as plain text.
      if (!/^https?:\/\//i.test(inline.href) && !parsed) {
        return <Fragment key={key}>{children}</Fragment>;
      }

      // Parity with the old sanitized path: every emitted anchor is a
      // new-tab anchor, regardless of the node's newTab flag.
      if (!parsed) {
        return (
          <a
            key={key}
            href={inline.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        );
      }

      // Hub article links load in the panel; market-level links (no slug)
      // open the hub section in a new tab.
      const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();

        if (parsed.slug) {
          onOpenArticle(parsed.slug, parsed.market);
          return;
        }

        window.open(
          `${KB_BASE_URL}/learn/${parsed.market}`,
          '_blank',
          'noopener,noreferrer',
        );
      };

      // target/rel keeps a middle-click or modifier-click (which bypasses
      // onClick) from opening the hub with window.opener set.
      return (
        <a
          key={key}
          href={inline.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClick}
        >
          {children}
        </a>
      );
    }
  }
};

const DocumentLinks = ({
  previewUrl,
  downloadUrl,
}: {
  previewUrl?: string;
  downloadUrl: string;
}) => (
  <>
    {previewUrl && (
      <a href={previewUrl} target="_blank" rel="noopener noreferrer">
        <FormattedMessage {...i18n.openDocument} />
      </a>
    )}
    <a href={downloadUrl} download target="_blank" rel="noopener noreferrer">
      <FormattedMessage {...i18n.downloadDocument} />
    </a>
  </>
);

type KbLinkNode = Extract<KbIrNode, { type: 'kb-link' }>;

// Table block (contract @2). Header cells render as th; col/row spans
// pass through; odd rows get a quiet stripe.
const DataTable = ({
  rows,
  onOpenArticle,
}: {
  rows: KbIrTableCell[][];
  onOpenArticle: OnOpenArticle;
}) => (
  <table className={styles.dataTable}>
    <tbody>
      {rows.map((row, rowIndex) => (
        <tr
          key={rowIndex}
          className={rowIndex % 2 === 1 ? styles.dataTableStripe : undefined}
        >
          {row.map((cell, cellIndex) => {
            const Tag = cell.header ? 'th' : 'td';

            return (
              <Tag
                key={cellIndex}
                colSpan={cell.colSpan}
                rowSpan={cell.rowSpan}
              >
                {renderInlines(cell.children, onOpenArticle)}
              </Tag>
            );
          })}
        </tr>
      ))}
    </tbody>
  </table>
);

const KbLinkChip = ({ node }: { node: KbLinkNode }) => {
  const intl = useIntl();

  return (
    // Hub resource links are employees-only surfaces: never intercepted,
    // always a new-tab anchor.
    <a
      className={styles.kbLinkChip}
      href={node.href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {`${intl.formatMessage(i18n.kbRelated)} · ${node.title || node.slug}`}
    </a>
  );
};

const BlockNode = ({
  node,
  onOpenArticle,
}: {
  node: KbIrNode;
  onOpenArticle: OnOpenArticle;
}): ReactNode => {
  switch (node.type) {
    case 'paragraph':
      return <p>{renderInlines(node.children, onOpenArticle)}</p>;
    case 'quote':
      return (
        <blockquote>{renderInlines(node.children, onOpenArticle)}</blockquote>
      );
    case 'heading': {
      const Tag = `h${node.level}` as 'h1' | 'h2' | 'h3' | 'h4';

      return <Tag>{renderInlines(node.children, onOpenArticle)}</Tag>;
    }
    case 'list': {
      const Tag = node.ordered ? 'ol' : 'ul';

      return (
        <Tag>
          {node.items.map((item, index) => (
            <li key={index}>{renderInlines(item, onOpenArticle)}</li>
          ))}
        </Tag>
      );
    }
    case 'table':
      return <DataTable rows={node.rows} onOpenArticle={onOpenArticle} />;
    case 'horizontalrule':
      return <hr />;
    case 'code':
      return (
        <pre>
          <code>{node.text}</code>
        </pre>
      );
    case 'image':
      if (node.variant === 'block') {
        // Asset captions are intentionally not rendered for now.
        return (
          <figure className={styles.mediaFigure}>
            <img
              src={node.src}
              alt={node.alt}
              loading="lazy"
              onError={collapseOnError}
            />
          </figure>
        );
      }

      return (
        <img
          src={node.src}
          alt={node.alt}
          loading="lazy"
          onError={collapseOnError}
        />
      );
    case 'video':
      if (node.variant === 'block') {
        return (
          <figure className={styles.mediaFigure}>
            <video
              src={node.src}
              poster={node.poster}
              controls
              preload="metadata"
            />
          </figure>
        );
      }

      return <video src={node.src} controls preload="metadata" />;
    case 'youtube':
      return (
        <figure className={styles.mediaFigure}>
          <iframe
            src={`https://www.youtube.com/embed/${node.videoId}`}
            title={node.caption || 'YouTube video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        </figure>
      );
    case 'document': {
      // Upload-embedded documents render as a plain paragraph download
      // link; block documents keep the card presentation.
      if (node.variant !== 'block') {
        return (
          <p>
            <a
              href={node.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {node.label || node.title || node.filename}
            </a>
          </p>
        );
      }

      const title = node.title || node.filename;

      if (node.previewUrl) {
        return (
          <figure className={styles.mediaFigure}>
            <iframe
              className={styles.documentFrame}
              src={node.previewUrl}
              title={title}
              loading="lazy"
            />
            <figcaption className={styles.documentLinksRow}>
              <span className={styles.documentTitle}>{title}</span>
              <DocumentLinks
                previewUrl={node.previewUrl}
                downloadUrl={node.downloadUrl}
              />
            </figcaption>
          </figure>
        );
      }

      return (
        <figure className={styles.documentCard}>
          <span className={styles.documentTitle}>{title}</span>
          <div className={styles.documentLinksRow}>
            <DocumentLinks downloadUrl={node.downloadUrl} />
          </div>
        </figure>
      );
    }
    case 'kb-link': {
      // Hub resource links are employees-only surfaces: never intercepted,
      // always a new-tab anchor.
      return <KbLinkChip node={node} />;
    }
  }
};

type KbIrRendererProps = {
  nodes: KbIrNode[];
  onOpenArticle: OnOpenArticle;
};

export const KbIrRenderer = ({ nodes, onOpenArticle }: KbIrRendererProps) => (
  <div className={styles.articleContent}>
    {nodes.map((node, index) => (
      <BlockNode key={index} node={node} onOpenArticle={onOpenArticle} />
    ))}
  </div>
);
